import { useEffect, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { api, money } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const errorText = (e) =>
  typeof e?.response?.data?.detail === "string"
    ? e.response.data.detail
    : "Unable to complete the request. Please try again.";
export function VisaDetail() {
  const { slug } = useParams();
  const { user } = useAuth();
  const [guide, setGuide] = useState(null),
    [error, setError] = useState(""),
    [busy, setBusy] = useState(false),
    [saved, setSaved] = useState(null);
  const [form, setForm] = useState({
    nationality: "",
    country_of_residence: "",
    phone: "",
    travel_date: "",
    message: "",
    consent_to_contact: false,
  });
  const sending = useRef(false);
  useEffect(() => {
    let active = true;
    setGuide(null);
    setError("");
    setSaved(null);
    api
      .get(`/visas/guides/${slug}`)
      .then((r) => {
        if (active) setGuide(r.data);
      })
      .catch((e) => {
        if (active) setError(errorText(e));
      });
    return () => {
      active = false;
    };
  }, [slug]);
  const send = async (e) => {
    e.preventDefault();
    if (sending.current || saved) return;
    sending.current = true;
    setBusy(true);
    setError("");
    try {
      const r = await api.post("/visas/requests", {
        ...form,
        guide_slug: slug,
        destination: guide.country,
      });
      setSaved(r.data);
    } catch (e) {
      setError(errorText(e));
    } finally {
      sending.current = false;
      setBusy(false);
    }
  };
  if (!guide)
    return (
      <div className="max-w-5xl mx-auto p-10" role="status">
        {error || "Loading visa guide…"}
      </div>
    );
  return (
    <main className="max-w-5xl mx-auto px-5 py-10">
      <Link to="/visas" className="text-blue-800 underline">
        All visa guides
      </Link>
      <p className="overline mt-6">
        {guide.country} · {guide.category}
      </p>
      <h1 className="text-3xl font-semibold mt-2">{guide.title}</h1>
      <p className="mt-3 text-slate-600">{guide.summary}</p>
      <div className="grid gap-5 md:grid-cols-2 mt-8">
        {[
          ["Eligibility", guide.eligibility],
          ["Government fees", guide.government_fees],
          ["Expected decision time", guide.processing_time],
          ["Other costs to plan for", guide.additional_costs],
        ].map(([title, body]) => (
          <section key={title} className="card p-6">
            <h2 className="font-semibold">{title}</h2>
            <p className="text-sm text-slate-600 mt-3">{body}</p>
          </section>
        ))}
      </div>
      <section className="card p-6 mt-5">
        <h2 className="font-semibold">Documents to prepare</h2>
        <ul className="list-disc pl-5 mt-3 text-sm space-y-2">
          {guide.documents.map((d) => (
            <li key={d}>{d}</li>
          ))}
        </ul>
        <p className="text-xs text-slate-500 mt-4">
          Your authority's personalised checklist may request further evidence.
        </p>
      </section>
      <div className="grid gap-5 md:grid-cols-2 mt-5">
        {[
          ["Official application process", guide.application_steps],
          ["How EuropEdu support works", guide.support_steps],
        ].map(([title, steps]) => (
          <section key={title} className="card p-6">
            <h2 className="font-semibold">{title}</h2>
            <ol className="list-decimal pl-5 mt-3 text-sm space-y-3">
              {steps.map((s) => (
                <li key={s}>{s}</li>
              ))}
            </ol>
          </section>
        ))}
      </div>
      <section className="card p-6 mt-5" id="request-support">
        <h2 className="text-xl font-semibold">
          Request visa application support
        </h2>
        <p className="text-sm text-slate-600 mt-3">
          {guide.service_charge} Support covers the agreed document preparation
          and administrative assistance. Personal immigration advice, if needed,
          requires an appropriately authorised adviser.
        </p>
        {!user ? (
          <Link
            className="btn btn-primary mt-5"
            to={`/login?next=${encodeURIComponent(`/visas/${slug}#request-support`)}`}
          >
            Sign in to send a request
          </Link>
        ) : saved ? (
          <div className="mt-5" role="status">
            <p>Support request received. Reference: {saved.id}</p>
            <Link className="btn btn-primary mt-3" to="/visa-support">
              Track my request
            </Link>
          </div>
        ) : (
          <form onSubmit={send} className="mt-5 space-y-4">
            <div className="grid gap-4 md:grid-cols-2">
              {[
                ["nationality", "Nationality"],
                ["country_of_residence", "Country of residence"],
                ["phone", "Contact telephone"],
              ].map(([key, label]) => (
                <label className="text-sm" key={key}>
                  {label}
                  <input
                    className="field mt-1"
                    required
                    minLength={key === "phone" ? 5 : 2}
                    maxLength={key === "phone" ? 40 : 100}
                    value={form[key]}
                    onChange={(e) =>
                      setForm({ ...form, [key]: e.target.value })
                    }
                  />
                </label>
              ))}
              <label className="text-sm">
                Intended travel date (optional)
                <input
                  className="field mt-1"
                  type="date"
                  value={form.travel_date}
                  onChange={(e) =>
                    setForm({ ...form, travel_date: e.target.value })
                  }
                />
              </label>
            </div>
            <label className="block text-sm">
              Tell us your plans and the support you need
              <textarea
                className="field mt-1"
                required
                minLength={10}
                maxLength={4000}
                rows={4}
                value={form.message}
                onChange={(e) => setForm({ ...form, message: e.target.value })}
              />
            </label>
            <label className="flex gap-2 text-sm">
              <input
                type="checkbox"
                required
                checked={form.consent_to_contact}
                onChange={(e) =>
                  setForm({ ...form, consent_to_contact: e.target.checked })
                }
              />
              I agree to EuropEdu contacting me about this request.
            </label>
            <button className="btn btn-primary" disabled={busy}>
              {busy ? "Sending…" : "Send free support enquiry"}
            </button>
          </form>
        )}
        {error && (
          <p role="alert" className="text-red-700 mt-4">
            {error}
          </p>
        )}
      </section>
      <section className="mt-8 text-xs text-slate-500">
        <p>
          Official sources checked {guide.last_reviewed}. {guide.disclaimer}
        </p>
        <ul className="mt-3 space-y-2">
          {guide.sources.map((url) => (
            <li key={url}>
              <a
                className="text-blue-800 underline break-all"
                href={url}
                target="_blank"
                rel="noreferrer"
              >
                {url}
              </a>
            </li>
          ))}
        </ul>
      </section>
    </main>
  );
}

export function VisaDocuments({ requestId, closed = false }) {
  const [files, setFiles] = useState([]),
    [error, setError] = useState(""),
    [busy, setBusy] = useState(false);
  useEffect(() => {
    api
      .get("/files", {
        params: { entity_type: "visa_request", entity_id: requestId },
      })
      .then((r) => setFiles(r.data))
      .catch((e) => setError(errorText(e)));
  }, [requestId]);
  const upload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setBusy(true);
    setError("");
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("entity_type", "visa_request");
      fd.append("entity_id", requestId);
      fd.append("category", "visa_support_document");
      await api.post("/files", fd);
      setFiles(
        (
          await api.get("/files", {
            params: { entity_type: "visa_request", entity_id: requestId },
          })
        ).data,
      );
    } catch (e) {
      setError(errorText(e));
    } finally {
      setBusy(false);
      e.target.value = "";
    }
  };
  const download = async (f) => {
    try {
      const r = await api.get(`/files/${f.id}/download`, {
        responseType: "blob",
      });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = f.original_filename;
      a.click();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch (e) {
      setError(errorText(e));
    }
  };
  return (
    <section className="mt-5">
      <h3 className="font-semibold">Application documents</h3>
      <p className="text-xs text-slate-500 mt-2">
        Upload only the documents requested for your agreed service. PDF, DOCX,
        JPG or PNG, up to 25 MB.
      </p>
      {!closed && (
        <label className="block mt-3 text-sm">
          Upload a document
          <input
            className="block mt-2"
            type="file"
            accept=".pdf,.docx,.jpg,.jpeg,.png"
            disabled={busy}
            onChange={upload}
          />
        </label>
      )}
      <ul className="space-y-2 mt-3">
        {files.map((f) => (
          <li key={f.id} className="text-sm">
            <button
              type="button"
              className="text-blue-800 underline"
              onClick={() => download(f)}
            >
              {f.original_filename}
            </button>{" "}
            · {f.malware_scan_status?.replace(/_/g, " ")}
          </li>
        ))}
      </ul>
      {busy && <p role="status">Securing document…</p>}
      {error && (
        <p role="alert" className="text-red-700">
          {error}
        </p>
      )}
    </section>
  );
}
export default function VisaWorkspace() {
  const [items, setItems] = useState([]),
    [error, setError] = useState(""),
    [loading, setLoading] = useState(true),
    [paying, setPaying] = useState("");
  useEffect(() => {
    api
      .get("/visas/requests/mine")
      .then((r) => setItems(r.data))
      .catch((e) => setError(errorText(e)))
      .finally(() => setLoading(false));
  }, []);
  const pay = async (id) => {
    setPaying(id);
    setError("");
    try {
      const r = await api.post("/payments/checkout", {
        item_type: "visa_request",
        item_id: id,
      });
      window.location.assign(r.data.checkout_url);
    } catch (e) {
      setError(errorText(e));
    } finally {
      setPaying("");
    }
  };
  return (
    <main className="max-w-5xl mx-auto px-5 py-10">
      <h1 className="text-3xl font-semibold">My visa support requests</h1>
      <p className="text-sm text-slate-600 mt-3">
        Follow your EuropEdu service, review quotations and upload documents
        after payment. Government application fees are separate.
      </p>
      <Link className="btn btn-ghost mt-4" to="/visas">
        Browse visa routes
      </Link>
      {loading && <p role="status">Loading requests…</p>}
      {error && (
        <p role="alert" className="text-red-700 mt-4">
          {error}
        </p>
      )}
      {!loading && !error && !items.length && (
        <p className="mt-6">
          No support requests yet. Choose a visa route to send an enquiry.
        </p>
      )}
      <div className="space-y-5 mt-6">
        {items.map((r) => (
          <article className="card p-6" key={r.id}>
            <h2 className="text-xl font-semibold">{r.visa_title}</h2>
            <p className="text-xs text-slate-500 mt-1">Reference {r.id}</p>
            <p className="pill bg-blue-50 text-blue-800 mt-3">
              {r.status.replace(/_/g, " ")}
            </p>
            <p className="text-sm mt-3">Payment: {r.payment_status}</p>
            {r.quote && (
              <section className="bg-slate-50 rounded-lg p-4 mt-4">
                <h3 className="font-semibold">
                  Your service quotation: {money(r.quote.price_pence)}
                </h3>
                <p className="text-sm mt-2">{r.quote.scope}</p>
                <p className="text-xs text-slate-500 mt-2">
                  EuropEdu service charge only; government fees are paid
                  separately.
                </p>
                {r.status === "awaiting_payment" &&
                  r.payment_status !== "paid" && (
                    <button
                      className="btn btn-primary mt-4"
                      disabled={Boolean(paying)}
                      onClick={() => pay(r.id)}
                    >
                      {paying === r.id
                        ? "Opening checkout…"
                        : "Accept quotation and pay securely"}
                    </button>
                  )}
              </section>
            )}
            {r.payment_status === "paid" && (
              <VisaDocuments
                requestId={r.id}
                closed={["closed", "decision_recorded"].includes(r.status)}
              />
            )}
            <h3 className="font-semibold mt-5">Progress timeline</h3>
            <ol className="border-l-2 border-blue-100 pl-5 mt-3 space-y-4">
              {(r.timeline || []).map((t, n) => (
                <li key={n}>
                  <p className="text-sm font-medium">
                    {t.status.replace(/_/g, " ")}
                  </p>
                  <p className="text-sm text-slate-600">{t.note}</p>
                  <p className="text-xs text-slate-500">
                    {new Date(t.at).toLocaleString("en-GB")}
                  </p>
                </li>
              ))}
            </ol>
          </article>
        ))}
      </div>
    </main>
  );
}
